import './HomePage.css';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from '@emotion/react';
import { Button } from '@mui/material';
import pacbio from '../../images/homepage/illumina-i100-pacbio-vega.png';
import ashi_logo from '../../images/homepage/ashi.png';
import PublicationsCard from '../../components/PublicationsCard/PublicationsCard';
import ServiceHomePageCard from '../../components/ServiceHomePageCard/ServiceHomePageCard';

const AREAS = ['Transplantation', 'Autoimmunity', 'Infectious Disease', 'Cancer'];
/* The areas fade in one after another once the title has finished, which is
   at ~1.74s (see the title timings below). Each fade is 0.6s (.main-description
   span in HomePage.css) and they overlap by half, so the last is fully in at
   1.8 + 3 x 0.3 + 0.6 = 3.3s. */
const AREA_BASE_DELAY = 1.8;
const AREA_STAGGER = 0.3;

const TITLE = 'Genotyping the Immune System';
/* Every word bursts orange; these are the ones that fall back to black
   afterwards, leaving "Genotyping ... Immune System" standing in orange. */
const RELEASE_WORDS = new Set(['the']);

/* Timings in seconds; the CSS durations they are matched against are
   .title-letter (0.35s) and .title-word-* (0.4s).

   Letters sweep first, then each word bursts once its own letters have
   landed. The last burst starts at 0.5 + 3 x 0.28 = 1.34s and runs 0.4s, so
   the line finishes at ~1.74s. */
const LETTER_STAGGER = 0.035;
const BURST_BASE = 0.5;
const BURST_STAGGER = 0.28;

/* One span per letter so each can carry its own animation-delay, wrapped in
   per-word spans that both hold the word together at a line break and carry
   the burst. The letter index runs across the whole line, not per word, so
   the fade reads as a single sweep. */
const renderTitleLetters = () => {
  let letterIndex = 0;
  const words = TITLE.split(' ');

  return words.flatMap((word, w) => {
    const letters = [...word].map((character, c) => (
      <span
        key={`${w}-${c}`}
        className='title-letter'
        style={{ animationDelay: `${(letterIndex++ * LETTER_STAGGER).toFixed(3)}s` }}
      >
        {character}
      </span>
    ));

    const burstClass = RELEASE_WORDS.has(word)
      ? 'title-word-release'
      : 'title-word-hold';

    const wordSpan = (
      <span
        key={`${word}-${w}`}
        className={`title-word ${burstClass}`}
        style={{ animationDelay: `${(BURST_BASE + w * BURST_STAGGER).toFixed(2)}s` }}
      >
        {letters}
      </span>
    );

    // Space lives between the word spans, never inside one, so the nowrap on
    // .title-word does not also glue the words to each other.
    return w < words.length - 1 ? [wordSpan, ' '] : [wordSpan];
  });
};

const HomePage = ({ service_cards, publications, theme }) => {
  return (
    <>
      <Helmet>
        <title>Scisco Genetics - Genotyping the Immune System</title>
        <meta
          name='description'
          content='Scisco Genetics provides NGS genotyping service and kits for HLA, KIR, FcGR and more.  Here you will find an introduction into products and services that Scisco Genetics provides.'
        />
      </Helmet>
      <header className='home-page-header'>
        {/* aria-label carries the whole line and the split spans are hidden:
            without this, screen readers announce the per-letter spans one
            character at a time. */}
        <h1 className='home-page-title' aria-label={TITLE}>
          <span aria-hidden='true'>{renderTitleLetters()}</span>
        </h1>
        {/* One span per application area so "Infectious Disease" is a single
            unit — as plain text the run-together spacing read as five terms
            and could break between the two words. */}
        <h4 className='main-description'>
          {AREAS.map((area, i) => (
            <span
              key={area}
              style={{
                animationDelay: `${(AREA_BASE_DELAY + i * AREA_STAGGER).toFixed(2)}s`,
              }}
            >
              {area}
            </span>
          ))}
        </h4>
      </header>

      <div className='homepage'>
        <section className='services-container'>
          <div className='services-info'>
            <span className='home-badge'>Our Products and services</span>
            <p className='services-heading'>
              Scisco Genetics Inc. is committed to providing the highest
              possible standard of testing to our clients
            </p>
            <p className='services-description'>
              Therefore, we have implemented a quality assurance program to
              ensure continuous improvement in our performance and accurate and
              reliable test results for our customers. Given good quality DNA
              samples, our quality metric is to obtain 100% accuracy in typing.
            </p>
          </div>
          <ServiceHomePageCard service_cards={service_cards} />
        </section>

        {/* Full-width gray band behind the 2nd and 4th sections, matching
            the white/gray alternation on the other pages. The sections
            themselves stay width-capped inside it. */}
        <div className='home-band'>
          <section className='ngs-container'>
            <div className='ngs'>
              <div className='ngs-info'>
                <span className='home-badge'>NGS</span>
                <h4 className='ngs-title titles'>Next Generation Sequencing</h4>
              </div>
              <p className='ngs-desc'>
                Scisco Genetics has developed complementary short- and long-read
                next-generation sequencing (NGS) workflows. The short-read
                workflow uses the Illumina MiSeq, which employs
                sequencing-by-synthesis with fluorescently labeled
                reversible-terminator nucleotides. Following assay-specific
                amplification, samples are tagged with unique indexes, pooled,
                amplified as individual clusters, and sequenced using universal
                sequencing primers. The long-read workflow uses the PacBio Vega
                system and highly accurate HiFi sequencing to generate reads that
                span larger regions of HLA and KIR genes, improving phasing and
                resolving complex or highly similar alleles that can be difficult
                to distinguish with short reads. Data from either platform can be
                analyzed using ScisCloud™ to provide unambiguous HLA allele typing
                and KIR haplotyping for scores to hundreds of samples
                simultaneously.
              </p>
            </div>
            <img className='tech-background' src={pacbio} alt='tech' />
          </section>
        </div>

        <section className='cert-container'>
          <div className='cert'>
            <p className='cert-desc'>
              Our quality assurance program has been assessed and approved by
              the American Society for Histocompatibility and Immunogenetics
              (ASHI) and the Clinical Laboratory Improvements Amendments (CLIA).
            </p>
            <img src={ashi_logo} className='logos' alt='ashi logo' />
          </div>
        </section>

        <div className='home-band'>
          <section className='company-news-container'>
            <div className='company-news-info'>
              <span className='home-badge'>Applications of Scisco's Technology</span>
              <h4 className='titles'>Company insights and industry information</h4>
            </div>
            <div className='publications-container'>
              <PublicationsCard publications={publications.slice(0, 3)} />
              <ThemeProvider theme={theme}>
                <Button
                  href='/resources_&_publications/#publications'
                  className='view-pubs-button'
                  color='primary'
                  variant='contained'
                  sx={{ borderRadius: '30px', fontWeight: 700 }}
                >
                  View all publications
                </Button>
              </ThemeProvider>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default HomePage;
